import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, doc, query, where, Timestamp, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import SearchIcon from '../../../components/icons/SearchIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import TimerIcon from '../../../components/icons/TimerIcon';
import CheckboxIcon from '../../../components/icons/CheckboxIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';

interface Vote {
  id: string;
  title: string;
  description: string;
  options: string[];
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  createdBy: string;
  schoolId: string;
  classId: string;
  resultVisibility: string;
}

interface VoteResult {
  id: string;
  voteId: string;
  studentId: string;
  option: string;
  timestamp: Timestamp;
}

const Vote: React.FC = () => {
  const { currentUser } = useAuth();
  const [votes, setVotes] = useState<Vote[]>([]);
  const [voteResults, setVoteResults] = useState<VoteResult[]>([]); // 현재 사용자의 투표 결과
  const [allVoteResults, setAllVoteResults] = useState<VoteResult[]>([]); // 모든 투표 결과 (통계용)
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  useEffect(() => {
    fetchVotes();
  }, [currentUser?.schoolId, currentUser?.classId]);

  useEffect(() => {
    if (votes.length > 0) {
      fetchVoteResults();
      fetchAllVoteResults();
    }
  }, [votes, currentUser?.schoolId, currentUser?.classId]);

  const fetchVotes = async () => {
    if (!currentUser?.schoolId || !currentUser?.classId) return;

    try {
      const votesRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/votes`);
      const q = query(votesRef);
      const snapshot = await getDocs(q);
      const votesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate(),
      })) as Vote[];
      setVotes(votesData);
      setLoading(false);
    } catch (error) {
      console.error('투표 목록 조회 실패:', error);
      setError('투표 목록을 불러오는데 실패했습니다.');
      setLoading(false);
    }
  };

  const fetchVoteResults = async () => {
    if (!currentUser?.schoolId || !currentUser?.classId || votes.length === 0) return;

    try {
      const resultsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/voteResults`);
      const q = query(
        resultsRef,
        where('studentId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(q);
      const resultsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as VoteResult[];
      setVoteResults(resultsData);
      
      // 선택된 옵션 초기화
      const selected: { [key: string]: string } = {};
      resultsData.forEach(result => {
        selected[result.voteId] = result.option;
      });
      setSelectedOptions(selected);
    } catch (error) {
      console.error('투표 결과 조회 실패:', error);
    }
  };

  const fetchAllVoteResults = async () => {
    if (!currentUser?.schoolId || !currentUser?.classId || votes.length === 0) return;

    try {
      const resultsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/voteResults`);
      const snapshot = await getDocs(resultsRef);
      const resultsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as VoteResult[];
      setAllVoteResults(resultsData);
    } catch (error) {
      console.error('전체 투표 결과 조회 실패:', error);
    }
  };

  const handleVote = async (voteId: string) => {
    if (!currentUser?.uid || !currentUser?.schoolId || !currentUser?.classId) return;

    try {
      const selectedOption = selectedOptions[voteId];
      if (!selectedOption) {
        setError('투표 항목을 선택해주세요.');
        return;
      }

      // 기존 투표 결과 삭제
      const existingResult = voteResults.find(result => result.voteId === voteId);
      if (existingResult) {
        await deleteDoc(doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/voteResults`, existingResult.id));
      }

      // 새로운 투표 결과 추가
      const voteResult = {
        voteId,
        studentId: currentUser.uid,
        option: selectedOption,
        timestamp: Timestamp.now()
      };

      await addDoc(collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/voteResults`), voteResult);
      setSuccess('투표가 완료되었습니다.');
      setTimeout(() => setSuccess(null), 3000);
      fetchVoteResults();
      fetchAllVoteResults();
    } catch (error) {
      console.error('투표 실패:', error);
      setError('투표 처리 중 오류가 발생했습니다.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleOptionChange = (voteId: string, option: string) => {
    setSelectedOptions(prev => ({
      ...prev,
      [voteId]: option,
    }));
  };

  const getVoteResultsForVote = (voteId: string) => {
    return allVoteResults.filter(result => result.voteId === voteId);
  };

  const hasVoted = (voteId: string) => {
    return voteResults.some(result => result.voteId === voteId);
  };

  const getVotedOption = (voteId: string) => {
    const result = voteResults.find(result => result.voteId === voteId);
    return result?.option;
  };

  const canShowResults = (vote: Vote) => {
    if (!vote.resultVisibility || vote.resultVisibility === 'always') return true;
    if (vote.resultVisibility === 'afterVote') return hasVoted(vote.id);
    if (vote.resultVisibility === 'afterClose') return !vote.isActive;
    if (vote.resultVisibility === 'private') return false;
    return true;
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const getRemainingDays = (endDate: Date) => {
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  // 필터링된 투표 목록
  const filteredVotes = useMemo(() => {
    let filtered = votes;

    // 필터 적용
    if (filter === 'active') {
      filtered = filtered.filter(vote => vote.isActive);
    } else if (filter === 'completed') {
      filtered = filtered.filter(vote => !vote.isActive);
    }

    // 검색어 필터링
    if (searchQuery) {
      filtered = filtered.filter(vote => 
        vote.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [votes, filter, searchQuery]);

  // 페이지네이션
  const totalPages = Math.ceil(filteredVotes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVotes = filteredVotes.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // 활성 투표 수
  const activeVoteCount = votes.filter(vote => vote.isActive).length;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--Color-background-main, #F4F6FA)'
      }}>
        <div style={{
          fontSize: '14px',
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--font-family-primary)'
        }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      backgroundColor: 'var(--Color-background-main, #F4F6FA)',
      minHeight: '100vh',
      fontFamily: 'var(--font-family-primary)'
    }}>
      {/* 제목 */}
      <div style={{
        marginBottom: '24px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 600,
          lineHeight: '48px',
          letterSpacing: '-0.64px',
          color: 'var(--Color-text-normal, #161616)',
          margin: 0
        }}>
          투표
        </h1>
      </div>

      {/* 투표 수 및 필터 */}
      <div style={{
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '27px',
            letterSpacing: '-0.18px',
            color: 'var(--Color-text-normal, #161616)',
            display: 'flex',
            gap: '4px',
            alignItems: 'center'
          }}>
            <span>총</span>
            <span style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>{activeVoteCount}</span>
            <span>개의 투표가 진행중입니다</span>
          </div>

          {/* 필터 라디오 버튼 */}
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
          }}>
            {(['all', 'active', 'completed'] as const).map((filterOption) => {
              const labels = {
                all: '전체',
                active: '진행 중',
                completed: '완료'
              };
              const isSelected = filter === filterOption;
              return (
                <div
                  key={filterOption}
                  onClick={() => {
                    setFilter(filterOption);
                    setCurrentPage(1);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isSelected ? (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="#FF6F0F" strokeWidth="1.2" />
                        <circle cx="8" cy="8" r="3" fill="#FF6F0F" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="#E9E9E9" strokeWidth="1.2" />
                      </svg>
                    )}
                  </div>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '18px',
                    color: isSelected ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                  }}>
                    {labels[filterOption]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 검색바 */}
      <div style={{
        marginBottom: '16px'
      }}>
        <div style={{
          width: '100%',
          height: '40px',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '8px',
          paddingBottom: '8px',
          background: 'white',
          borderRadius: '999px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <SearchIcon width={20} height={20} color="#222222" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="투표명 검색"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: searchQuery ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '13px',
              fontFamily: 'var(--font-family-primary)',
              fontWeight: 400,
              lineHeight: '19.5px'
            }}
          />
        </div>
      </div>

      {/* 에러/성공 메시지 */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#E8F5E9',
          color: '#2E7D32',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {success}
        </div>
      )}

      {/* 투표 카드 그리드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '40px'
      }}>
        {paginatedVotes.map((vote) => (
          <VoteCard
            key={vote.id}
            vote={vote}
            selectedOption={selectedOptions[vote.id]}
            onOptionChange={(option) => handleOptionChange(vote.id, option)}
            onVote={() => handleVote(vote.id)}
            hasVoted={hasVoted(vote.id)}
            votedOption={getVotedOption(vote.id)}
            canShowResults={canShowResults(vote)}
            formatDate={formatDate}
            getRemainingDays={getRemainingDays}
            voteResultsForVote={getVoteResultsForVote(vote.id)}
          />
        ))}
      </div>

      {/* 페이지네이션 */}
      {totalPages >= 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          paddingTop: '20px'
        }}>
          <div
            onClick={goToFirstPage}
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            <LeftArrowDoubleIcon width={24} height={24} color={currentPage === 1 ? '#989898' : '#161616'} />
          </div>
          <div
            onClick={goToPreviousPage}
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            <LeftArrowIcon width={24} height={24} color={currentPage === 1 ? '#989898' : '#161616'} />
          </div>
          
          {/* 페이지 번호 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0'
          }}>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 3) {
                pageNum = i + 1;
              } else if (currentPage === 1) {
                pageNum = i + 1;
              } else if (currentPage === totalPages) {
                pageNum = totalPages - 2 + i;
              } else {
                pageNum = currentPage - 1 + i;
              }
              const isSelected = currentPage === pageNum;
              return (
                <div
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isSelected ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                    cursor: 'pointer',
                    color: isSelected ? 'white' : 'var(--Color-text-assistive, #989898)',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family-primary)',
                    fontWeight: isSelected ? 500 : 400,
                    lineHeight: '14px'
                  }}
                >
                  {pageNum}
                </div>
              );
            })}
          </div>

          <div
            onClick={goToNextPage}
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.5 : 1
            }}
          >
            <RightArrowIcon width={24} height={24} color={currentPage === totalPages ? '#989898' : '#161616'} />
          </div>
          <div
            onClick={goToLastPage}
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.5 : 1
            }}
          >
            <RightArrowDoubleIcon width={24} height={24} color={currentPage === totalPages ? '#989898' : '#161616'} />
          </div>
        </div>
      )}
    </div>
  );
};

// VoteCard 컴포넌트
interface VoteCardProps {
  vote: Vote;
  selectedOption: string | undefined;
  onOptionChange: (option: string) => void;
  onVote: () => void;
  hasVoted: boolean;
  votedOption?: string;
  canShowResults: boolean;
  formatDate: (date: Date) => string;
  getRemainingDays: (date: Date) => number;
  voteResultsForVote: VoteResult[];
}

const VoteCard: React.FC<VoteCardProps> = ({
  vote,
  selectedOption,
  onOptionChange,
  onVote,
  hasVoted,
  votedOption,
  canShowResults,
  formatDate,
  getRemainingDays,
  voteResultsForVote
}) => {
  const getVoteCount = (option: string) => {
    return voteResultsForVote.filter(result => result.option === option).length;
  };

  const totalVotes = voteResultsForVote.length;
  const getPercentage = (option: string) => {
    if (totalVotes === 0) return 0;
    return Math.round((getVoteCount(option) / totalVotes) * 100);
  };

  const remainingDays = getRemainingDays(vote.endDate);
  const isExpired = !vote.isActive;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 5px 0px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      height: '535px'
    }}>
      {/* 상태 칩 및 날짜 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: vote.isActive ? '#FFD2B5' : 'white',
          border: vote.isActive ? 'none' : '1px solid var(--Color-line-normal, #F0F0F0)',
          padding: '2px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '18px',
          color: vote.isActive ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
        }}>
          {vote.isActive ? '진행중' : '종료'}
        </div>
        <div style={{
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '18px',
          color: 'var(--Color-text-minor, #BABABA)'
        }}>
          {formatDate(vote.startDate)}~{formatDate(vote.endDate)}
        </div>
      </div>

      {/* 제목 및 설명 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: '27px',
          color: 'var(--Color-text-normal, #161616)',
          margin: 0
        }}>
          {vote.title}
        </h3>
        <p style={{
          fontSize: '13px',
          fontWeight: 500,
          lineHeight: '19.5px',
          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {vote.description}
        </p>
      </div>

      {/* 투표 옵션 */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <div style={{
          border: '1px solid var(--Color-line-normal, #F0F0F0)',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          flex: 1,
          overflow: 'hidden'
        }}>
          {/* 참여자 수 및 자세히 보기 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center'
            }}>
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '18px',
                color: 'var(--Color-fill-brand-color, #FF6F0F)'
              }}>
                {totalVotes}명
              </span>
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '18px',
                color: 'var(--Color-text-assistive, #989898)'
              }}>
                참여중
              </span>
            </div>
            <div style={{
              display: 'flex',
              gap: '2px',
              alignItems: 'center',
              cursor: 'pointer'
            }}>
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '18px',
                color: 'var(--Color-text-assistive, #989898)'
              }}>
                자세히 보기
              </span>
              <RightArrowIcon width={16} height={16} color="#989898" />
            </div>
          </div>

          {/* 옵션 리스트 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            overflowY: 'auto',
            flex: 1
          }}>
            {vote.options.map((option, index) => {
              const isSelected = selectedOption === option;
              const isVotedOption = hasVoted && votedOption === option;
              const count = canShowResults ? getVoteCount(option) : 0;
              const percentage = canShowResults ? getPercentage(option) : 0;
              const showAsSelected = isSelected || isVotedOption;

              return (
                <div key={index} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div
                      onClick={() => {
                        if (vote.isActive && !hasVoted) {
                          onOptionChange(option);
                        }
                      }}
                      style={{
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                        cursor: vote.isActive && !hasVoted ? 'pointer' : 'default'
                      }}
                    >
                      <CheckboxIcon
                        width={16}
                        height={16}
                        checked={showAsSelected}
                        color={vote.isActive ? '#293044' : '#989898'}
                        checkedColor="#FF6F0F"
                      />
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: '19.5px',
                        color: isVotedOption ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {option}
                      </span>
                    </div>
                    {canShowResults && totalVotes > 0 && (
                      <div style={{
                        display: 'flex',
                        gap: '5px',
                        alignItems: 'center',
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '19.5px'
                      }}>
                        <span style={{ color: '#000000' }}>{count}표</span>
                        <span style={{ color: 'var(--Color-text-assistive, #989898)' }}>({percentage}%)</span>
                      </div>
                    )}
                  </div>
                  {canShowResults && totalVotes > 0 && (
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#E9E9E9',
                      borderRadius: '999px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${percentage}%`,
                        backgroundColor: isVotedOption ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-fill-normal, #293044)',
                        borderRadius: '999px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 남은 시간 */}
          <div style={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: 'auto'
          }}>
            <TimerIcon width={16} height={16} color={vote.isActive ? '#FF6F0F' : '#989898'} />
            <span style={{
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '18px',
              color: vote.isActive ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
            }}>
              {vote.isActive ? (
                <>
                  <span>{remainingDays}일</span>
                  <span style={{ color: 'var(--Color-text-assistive, #989898)' }}> 후 종료</span>
                </>
              ) : (
                `${formatDate(vote.endDate)} 투표 종료`
              )}
            </span>
          </div>
        </div>

        {/* 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '16px'
        }}>
          {!hasVoted && vote.isActive && (
            <button
              onClick={onVote}
              disabled={!selectedOption}
              style={{
                height: '32px',
                padding: '8px 12px',
                backgroundColor: selectedOption ? 'var(--Color-fill-normal, #293044)' : '#EDEDED',
                color: selectedOption ? 'white' : 'var(--Color-text-minor, #BABABA)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '14px',
                cursor: selectedOption ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--font-family-primary)'
              }}
            >
              투표하기
            </button>
          )}
          {hasVoted && vote.isActive && (
            <button
              onClick={onVote}
              disabled={!selectedOption}
              style={{
                height: '32px',
                padding: '8px 12px',
                backgroundColor: selectedOption ? 'var(--Color-fill-normal, #293044)' : '#EDEDED',
                color: selectedOption ? 'white' : 'var(--Color-text-minor, #BABABA)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '14px',
                cursor: selectedOption ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--font-family-primary)'
              }}
            >
              다시 투표하기
            </button>
          )}
          {isExpired && (
            <button
              disabled
              style={{
                height: '32px',
                padding: '8px 12px',
                backgroundColor: '#EDEDED',
                color: 'var(--Color-text-minor, #BABABA)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '14px',
                cursor: 'not-allowed',
                fontFamily: 'var(--font-family-primary)'
              }}
            >
              종료
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Vote;
